import { HomeworkStatusChip } from "@/components/homework/HomeworkStatusChip";
import { Skeleton } from "@/components/shared/Skeleton";
import { AppButton } from "@/components/teacher/AppButton";
import type { SelectedSlide } from "@/components/teacher/SlidePickerDialog";
import { StatPill } from "@/components/teacher/StatPill";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { useFragment } from "@/generated";
import {
  DeleteHomeworkDocument,
  SlideFieldsFragmentDoc,
  StudentHomeworkDocument,
  type StudentHomeworkQuery,
} from "@/generated/graphql";
import { format, formatYearMonthDay, useLocale, useT } from "@/i18n";
import { useMutation, useQuery } from "@apollo/client/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import toast from "react-hot-toast";
import { AssignHomeworkModal } from "./AssignHomeworkModal";
import { StudentBreadcrumb } from "./StudentBreadcrumb";
import { CLASSROOM_ROOT, type ClassroomScope } from "./classroomScope";
import { useStudentContext } from "./useStudentContext";

const SKELETON_KEYS = ["a", "b", "c", "d"];

type AssignedHomework = StudentHomeworkQuery["studentHomework"][number];

/**
 * The picker's groups, rebuilt from an assignment's flat slide list: a subslide folds back into
 * the parent it was assigned with, and stands alone once that parent is gone from the homework.
 */
function toSelectedSlides(assigned: AssignedHomework["slides"]): SelectedSlide[] {
  const slides = useFragment(SlideFieldsFragmentDoc, assigned);
  const groups = new Map<string, SelectedSlide>();
  for (const slide of slides) {
    if (!slide.parentSlideId) groups.set(slide.id, { slide, slideIds: [slide.id] });
  }

  for (const slide of slides) {
    if (!slide.parentSlideId) continue;
    const parent = groups.get(slide.parentSlideId);
    if (parent) parent.slideIds.push(slide.id);
    else groups.set(slide.id, { slide, slideIds: [slide.id] });
  }

  return [...groups.values()];
}

/** Everything assigned to this student, done and not, soonest deadline first. */
export function StudentHomeworkListPage({
  classroomId,
  studentId,
  scope = "teacher",
}: { classroomId: string; studentId: string; scope?: ClassroomScope }) {
  const t = useT();
  const locale = useLocale();
  const root = CLASSROOM_ROOT[scope];
  const { classroomName, student } = useStudentContext(scope, classroomId, studentId);
  const { data, loading, error } = useQuery(StudentHomeworkDocument, {
    variables: { classroomId, studentId },
  });
  const [reassigning, setReassigning] = useState<AssignedHomework | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteHomework] = useMutation(DeleteHomeworkDocument, {
    refetchQueries: ["StudentHomework"],
  });

  const homework = data?.studentHomework ?? [];
  const completed = homework.filter((item) => item.isComplete).length;

  const handleDelete = async (item: AssignedHomework) => {
    const warning = item.completedSlides > 0 ? t["homework.deleteWarning"] : "";
    if (!window.confirm(format(t["homework.deleteConfirm"], { title: item.title, warning }))) {
      return;
    }

    setDeletingId(item.id);
    try {
      await deleteHomework({ variables: { classroomId, studentId, homeworkId: item.id } });
      toast.success(t["homework.deleted"]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t["homework.deleteFailed"]);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <TeacherPage
      title={student?.name ?? t["student.fallbackName"]}
      embedded={scope === "admin"}
      actions={
        <StatPill tone={completed === homework.length && completed > 0 ? "mint" : "aqua"}>
          {format(t["homework.completedOfTotal"], {
            completed,
            total: homework.length,
          })}
        </StatPill>
      }
      breadcrumb={
        <StudentBreadcrumb
          scope={scope}
          classroomId={classroomId}
          classroomName={classroomName}
          studentId={studentId}
          studentName={student?.name ?? null}
          trail={[t["student.homework"]]}
        />
      }
    >
      <div className="app-card divide-y divide-gray-100">
        {loading && homework.length === 0 ? (
          SKELETON_KEYS.map((key) => (
            <div key={key} className="p-4 sm:p-5">
              <Skeleton className="h-10" />
            </div>
          ))
        ) : error ? (
          <p className="body-sm p-4 text-app-overdue sm:p-6">{t["homework.listLoadError"]}</p>
        ) : homework.length === 0 ? (
          <p className="body-sm p-4 text-app-muted sm:p-6">{t["homework.listEmpty"]}</p>
        ) : (
          homework.map((item) => (
            <div
              key={item.id}
              className={`flex flex-col gap-2 px-4 py-3 first:rounded-t-card last:rounded-b-card sm:flex-row sm:items-center sm:gap-3 sm:px-5 sm:py-4 ${
                item.isComplete ? "bg-app-mint/20 hover:bg-app-mint/20" : "hover:bg-app-sky-soft"
              }`}
            >
              <Link
                to={`${root}/$classroomId/students/$studentId/homework/$homeworkId`}
                params={{ classroomId, studentId, homeworkId: item.id }}
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                <span className="flex shrink-0">
                  <HomeworkStatusChip isComplete={item.isComplete} isLate={item.isLate} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-kumbh text-base font-extrabold text-app-ink">
                    {item.title}
                  </span>
                  <span className="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                    {item.isComplete && item.lastPlayedAt ? (
                      <>
                        <span className="app-label">{t["homework.submitted"]}</span>
                        <span className="app-value">
                          {formatYearMonthDay(locale, new Date(item.lastPlayedAt))}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="app-label">{t["homework.due"]}</span>
                        <span
                          className={`app-value ${item.isLate && !item.isComplete ? "text-app-overdue" : ""}`}
                        >
                          {formatYearMonthDay(locale, new Date(item.dueAt))}
                        </span>
                      </>
                    )}
                    <span className="app-label ml-2">{t["homework.progress"]}</span>
                    <span className="app-value tabular-nums">
                      {item.completedSlides}/{item.totalSlides}
                    </span>
                  </span>
                </span>
                {typeof item.score === "number" && (
                  <span className="app-metric shrink-0 tabular-nums">{Math.round(item.score)}</span>
                )}
              </Link>

              <span className="flex shrink-0 items-center justify-end gap-2">
                <AppButton tone="solid" size="sm" onClick={() => setReassigning(item)}>
                  {t["homework.reassign"]}
                </AppButton>
                <AppButton
                  tone="quiet"
                  size="sm"
                  className="text-app-overdue"
                  isLoading={deletingId === item.id}
                  onClick={() => void handleDelete(item)}
                >
                  {t["common.delete"]}
                </AppButton>
              </span>
            </div>
          ))
        )}
      </div>

      {reassigning && (
        <AssignHomeworkModal
          classroomId={classroomId}
          scope={scope}
          initial={{
            title: reassigning.title,
            studentIds: [studentId],
            slides: toSelectedSlides(reassigning.slides),
          }}
          onClose={() => setReassigning(null)}
        />
      )}
    </TeacherPage>
  );
}
